import { useState, ReactNode } from 'react';
import { useLoginStore } from '../../store/useLogin';
import { useFranchiseStore } from '../../store/useFranchise';

export interface FilterTab {
  id: string;
  label: string;
  content: ReactNode;
}

export interface MobileFilterTabsProps {
  tabs: FilterTab[];
  /** 회사선택 기능 사용 여부 (Admin/CS만 표시) */
  showFranchiseTab?: boolean;
  /** 선택된 회사 ID */
  selectedFranchiseId?: string;
  /** 회사 선택 핸들러 */
  onFranchiseSelect?: (franchiseId: string) => void;
}

/**
 * @author ellie
 * @date 2026.02.25
 * @description 모바일 탭 형태 필터 컴포넌트
 */
const MobileFilterTabs = ({
  tabs,
  showFranchiseTab = false,
  selectedFranchiseId = '',
  onFranchiseSelect,
}: MobileFilterTabsProps) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  
  const { role } = useLoginStore();
  const { allFranchises } = useFranchiseStore();
  const isAdmin = role === 'ROLE_ADMIN' || role === 'ROLE_CS';

  const shouldShowFranchise = showFranchiseTab && isAdmin && onFranchiseSelect;

  const selectedFranchiseName = selectedFranchiseId
    ? allFranchises?.find(f => f.id === selectedFranchiseId)?.companyName || '회사선택'
    : '회사선택';

  return (
    <div className="md:!hidden">
      {/* 탭 버튼들 */}
      <div className="!flex !bg-white !border !border-gray-200 !rounded-lg !overflow-hidden">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(activeTab === tab.id ? null : tab.id)}
            className={`!flex-1 !py-2 !text-sm !font-medium ${index < tabs.length - 1 || shouldShowFranchise ? '!border-r !border-gray-200' : ''} ${activeTab === tab.id ? '!bg-blue-50 !text-blue-700' : '!text-gray-700'}`}
          >
            {tab.label} {activeTab === tab.id ? '▲' : '▼'}
          </button>
        ))}
        {shouldShowFranchise && (
          <button
            onClick={() => setActiveTab(activeTab === 'franchise' ? null : 'franchise')}
            className={`!flex-1 !py-2 !text-sm !font-medium !truncate !px-2 ${activeTab === 'franchise' ? '!bg-blue-50 !text-blue-700' : '!text-gray-700'}`}
          >
            {selectedFranchiseName} {activeTab === 'franchise' ? '▲' : '▼'}
          </button>
        )}
      </div>

      {/* 탭 콘텐츠 */}
      {tabs.map(tab => (
        activeTab === tab.id && (
          <div key={tab.id} className="!mt-2 !bg-white !border !border-gray-200 !rounded-lg !overflow-hidden">
            {tab.content}
          </div>
        )
      ))}

      {/* 회사선택 콘텐츠 */}
      {activeTab === 'franchise' && shouldShowFranchise && (
        <div className="!mt-2 !bg-white !border !border-gray-200 !rounded-lg !overflow-hidden">
          <div className="!max-h-48 !overflow-y-auto">
            <button
              onClick={() => { onFranchiseSelect(''); setActiveTab(null); }}
              className={`!w-full !px-3 !py-2 !text-left !text-sm !border-b ${!selectedFranchiseId ? '!bg-blue-50 !text-blue-700' : ''}`}
            >
              전체 회사
            </button>
            {(allFranchises || []).map(f => (
              <button
                key={f.id}
                onClick={() => { onFranchiseSelect(f.id); setActiveTab(null); }}
                className={`!w-full !px-3 !py-2 !text-left !text-sm !border-b ${selectedFranchiseId === f.id ? '!bg-blue-50 !text-blue-700' : ''}`}
              >
                <div>{f.companyName || f.id}</div>
                <div className="!text-xs !text-gray-500">{f.ceo || '-'} | {f.telNo || '-'}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileFilterTabs;
